import React, { useEffect, useState } from "react";
import { Box, Card, CardContent, Typography, TextField, Button, Alert, Grid, Paper } from "@mui/material";
import { getOnboardingQuizConfig, setOnboardingQuizConfig } from "../../services/quizService";
import { validateQuizJSON } from "../../utils/quizSchema";
import { logAdminAction } from "../../services/adminService";

export default function AdminQuizManager() {
  const [jsonText, setJsonText] = useState("{\n  \"questions\": []\n}");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    async function load() {
      try {
        const cfg = await getOnboardingQuizConfig();
        if (!active) return;
        if (cfg) {
          setJsonText(JSON.stringify(cfg, null, 2));
          setPreview(cfg);
        }
      } catch (e) {
        // ignore
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => { active = false; };
  }, []);

  const handleValidate = () => {
    setError("");
    setSuccess("");
    try {
      const obj = JSON.parse(jsonText);
      const res = validateQuizJSON(obj);
      if (!res.valid) {
        setError(res.error);
        setPreview(null);
        return;
      }
      setPreview(obj);
      setSuccess("Valid JSON");
    } catch (e) {
      setError("Invalid JSON: " + e.message);
      setPreview(null);
    }
  };

  const handleSave = async () => {
    setError("");
    setSuccess("");
    try {
      const obj = JSON.parse(jsonText);
      const res = validateQuizJSON(obj);
      if (!res.valid) {
        setError(res.error);
        setPreview(null);
        return;
      }
      await setOnboardingQuizConfig(obj, "ADMIN");
      await logAdminAction("save_quiz_config", "ADMIN", null, {});
      setSuccess("Quiz configuration saved");
    } catch (e) {
      setError(e.message || "Failed to save");
    }
  };

  return (
    <Card sx={{ borderTop: "6px solid #003366" }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 800, color: "#003366", mb: 2 }}>Onboarding Quiz Configuration</Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Quiz JSON"
              value={jsonText}
              onChange={(e) => setJsonText(e.target.value)}
              multiline
              minRows={16}
              fullWidth
              placeholder='{"questions":[{"id":"q1","text":"...","options":["A","B"],"correct":0,"credit":20}]}'
            />
            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
              <Button variant="outlined" onClick={handleValidate}>Validate</Button>
              <Button variant="contained" onClick={handleSave}>Save</Button>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Preview</Typography>
            {!preview ? (
              <Typography color="text.secondary">{loading ? "Loading..." : "No preview"}</Typography>
            ) : (
              <Box sx={{ maxHeight: 420, overflowY: "auto" }}>
                {preview.questions.map((q, idx) => (
                  <Paper key={q.id || idx} sx={{ p: 1.5, mb: 1, border: "1px solid #e5e7eb" }}>
                    <Typography sx={{ fontWeight: 600 }}>{idx + 1}. {q.text}</Typography>
                    <Box sx={{ pl: 2 }}>
                      {q.options.map((opt, i) => (
                        <Typography key={i} variant="body2">
                          - {opt} {((q.correct === i) || (q.correct === opt)) ? "(correct)" : ""}
                        </Typography>
                      ))}
                    </Box>
                    {q.credit != null && (
                      <Typography variant="caption" color="text.secondary">Credit: {q.credit}</Typography>
                    )}
                  </Paper>
                ))}
              </Box>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
